// src/app/services/brand-monitoring-ws.ts
import { type ProviderId } from "@/app/constants/models";

/**
 * Frontend should ONLY send supported providers to backend (for now).
 * Upcoming models stay UI-only.
 */
export const SUPPORTED_PROVIDERS: ProviderId[] = ["openai", "claude", "perplexity"];

export type BrandMonitoringWsStatus = "queued" | "running" | "done" | "error";

/**
 * New message types added:
 * - analysis_started: emitted once after server receives config
 * - progress: emitted as models complete (done/error)
 */
export type BrandMonitoringWsMessage =
  | { type: "analysis_started"; brand?: string; totalModels?: number; models?: (ProviderId | string)[] }
  | { type: "progress"; percent?: number; done?: number; total?: number; engine: ProviderId | string; }
  | { type: "model_status"; engine: ProviderId | string; status: BrandMonitoringWsStatus; message?: string; errorType?: string }
  | { type: "final_result"; data: BrandMonitoringResponse; errors?: Record<string, string> }
  | { type: "error"; message: string; errors?: Record<string, string> };

/** Confirmed analysis scope from the scope picker (or the intake heuristic). */
export interface ConfirmedScopePayload {
  type: "brand" | "category" | "product" | "entity";
  name: string;
  anchor?: { location: string; domain: string; descriptor: string } | null;
}

export interface BrandMonitoringRequest {
  brandName: string;
  selectedModels: ProviderId[];
  scope?: ConfirmedScopePayload | null;
}

export interface PerceptionData {
  overallScore: number;
  scoreChange?: string;
  tier: string;
  quote: string;
  modelsRecommended: number;
  accuracy: string;
  velocity: string;
  brandAssociations: string[];
  strengths: string[];
  weaknesses: string[];
}

export interface ModelComparisonItem {
  model: string; // provider id from backend: "openai" | "claude" | "perplexity"
  score: number;
  rank: number;
  velocity: number;
  velocityColor: string;
  accuracyRisk: string;
  riskColor: "success" | "warning" | "error";
}

export interface RecommendedAction {
  id: number;
  priority: "HIGH" | "MEDIUM" | "LOW";
  title: string;
  description: string;
  impact: string;
  effort: "Low" | "Medium" | "High";
}

export interface HeroMetricItem {
  percentage: number;
  calculation: string;
}

export interface HeroMetrics {
  referralConversations: HeroMetricItem;
  bestInCategory: HeroMetricItem;
  numberOneRecommendation: HeroMetricItem;
}

export interface CompetitivePositionItem {
  rank: number;
  brand: string;
  score: number;
  points: number;
  category: string;
  color: string;
}

export interface CompetitiveVelocityBrand {
  brand: string;
  change: number;
  fullName: string;
}

export interface CompetitiveVelocity {
  status: number;
  rising: CompetitiveVelocityBrand[];
  declining: CompetitiveVelocityBrand[];
}

export interface DataIntegrityIssue {
  type: string;
  count: number;
  severity: string;
  color: string;
  bgColor?: string;
}

export interface DataIntegrity {
  riskLevel: string;
  riskColor: string;
  issues: DataIntegrityIssue[];
  modelsAffected: string[];
  totalModels: number;
}

export interface AttributeSignalItem {
  attribute: string;
  emphasis: number;
}

export interface AttributeSignals {
  topEmphasized: AttributeSignalItem[];
  underEmphasized: AttributeSignalItem[];
}

/** A lane the run planned that produced no analysis, and why. */
export interface FailedModel {
  model: string;
  /** timeout | rate_limited | spend_limit | provider_error | empty_response
   *  | unparseable_response | not_runnable | no_result */
  errorType: string;
  reason: string;
}

export interface BrandMonitoringResponse {
  runId?: number;
  brandName: string;
  perceptionData: PerceptionData;
  /** Lanes that returned an analysis. */
  modelComparison: ModelComparisonItem[];
  /** Every lane the run set out to measure. Absent on runs saved before
   *  failures were recorded, which stored only the lanes that succeeded. */
  plannedModels?: string[];
  failedModels?: FailedModel[];
  recommendedActions: RecommendedAction[];
  heroMetrics: HeroMetrics;
  competitivePosition: CompetitivePositionItem[];
  competitiveVelocity: CompetitiveVelocity;
  dataIntegrity: DataIntegrity;
  attributeSignals: AttributeSignals;
  baselineSnapshot?: string | null;
}

/**
 * Create a WS connection and stream events back to caller.
 * This helper auto-sends the config payload on open.
 */
export function openBrandMonitoringSocket(params: {
  baseWsUrl?: string;
  token: string;
  brand: string;
  models: ProviderId[];
  scope?: ConfirmedScopePayload | null;
  onEvent: (msg: BrandMonitoringWsMessage) => void;
  onOpen?: () => void;
  onError?: (err: Event) => void;
  onClose?: (ev: CloseEvent) => void;
}): WebSocket {
  const { baseWsUrl, token, brand, models, scope, onEvent, onOpen, onError, onClose } = params;

  const url = `${baseWsUrl}/ws/brand-monitoring?token=${encodeURIComponent(token)}`;

  const socket = new WebSocket(url);

  socket.onopen = () => {
    onOpen?.();

    // backend expects first message to be the config JSON
    const payload: BrandMonitoringRequest = { brandName: brand, selectedModels: models };
    if (scope) payload.scope = scope;
    socket.send(JSON.stringify(payload));
  };

  socket.onerror = (e) => onError?.(e);
  socket.onclose = (ev) => onClose?.(ev);

  socket.onmessage = (event) => {
    try {
      const parsed = JSON.parse(event.data) as BrandMonitoringWsMessage;
      onEvent(parsed);
    } catch {
      onEvent({ type: "error", message: "Invalid WS message format from server." });
    }
  };

  return socket;
}
